import { useTranslation } from 'react-i18next'
import { getFullPath } from '../helpers/path'
import { IconBack, IconCalendar, IconCar, IconChevron, IconList, IconMap } from './Icons'
import type { Vehicle } from '../types'
import './VehicleNav.css'

type Props = {
  vehicles: Vehicle[]
  selectedId?: string
  onSelect: (v: Vehicle) => void
  startDate?: string
  endDate?: string
  onStartDate?: (v: string) => void
  onEndDate?: (v: string) => void
  mode?: 'list' | 'map'
  onMode?: (m: 'list' | 'map') => void
  /** period = Du/Au range; day = single date */
  calendar?: 'period' | 'day'
  day?: string
  onDay?: (v: string) => void
}

export function VehicleNav({
  vehicles,
  selectedId,
  onSelect,
  startDate,
  endDate,
  onStartDate,
  onEndDate,
  mode,
  onMode,
  calendar,
  day,
  onDay,
}: Props) {
  const { t } = useTranslation()
  const idx = Math.max(
    0,
    vehicles.findIndex((v) => String(v._id) === String(selectedId))
  )
  const current = vehicles[idx]

  const go = (next: number) => {
    const i = Math.max(0, Math.min(vehicles.length - 1, next))
    if (vehicles[i]) onSelect(vehicles[i])
  }

  const brandIcon = current ? getFullPath('brands', current.brandId?.icon) : undefined

  return (
    <div className="vnav">
      <div className="vnav__band">
        <button
          type="button"
          className="vnav__arrow"
          aria-label="Previous"
          disabled={idx <= 0}
          onClick={() => go(idx - 1)}
        >
          <IconBack size={18} />
        </button>
        <div className="vnav__pill">
          {brandIcon ? (
            <img src={brandIcon} alt="" className="vnav__brand" />
          ) : (
            <span className="vnav__brand vnav__brand--fallback">
              <IconCar size={16} />
            </span>
          )}
          <div className="vnav__copy">
            <strong>{current?.name ?? '—'}</strong>
            {current?.plateNumber ? <span className="plate-chip">{current.plateNumber}</span> : null}
          </div>
          <IconChevron size={16} className="vnav__chevron" />
        </div>
        <button
          type="button"
          className="vnav__arrow"
          aria-label="Next"
          disabled={idx >= vehicles.length - 1}
          onClick={() => go(idx + 1)}
        >
          <IconChevron size={18} />
        </button>
      </div>

      {(calendar || onMode) && (
        <div className="vnav__toolbar">
          {calendar === 'period' && onStartDate && onEndDate && (
            <label className="vnav__dates">
              <IconCalendar size={16} />
              <span>
                {t('Du')}{' '}
                <input type="date" value={startDate} onChange={(e) => onStartDate(e.target.value)} />
                {' '}
                {t('Au')}{' '}
                <input type="date" value={endDate} onChange={(e) => onEndDate(e.target.value)} />
              </span>
            </label>
          )}
          {calendar === 'day' && onDay && (
            <label className="vnav__dates">
              <IconCalendar size={16} />
              <input type="date" value={day} onChange={(e) => onDay(e.target.value)} />
            </label>
          )}
          {onMode && (
            <div className="mob-toggle">
              <button
                type="button"
                className={mode === 'list' ? 'is-on' : undefined}
                onClick={() => onMode('list')}
                aria-label={t('Liste')}
              >
                <IconList size={16} />
              </button>
              <button
                type="button"
                className={mode === 'map' ? 'is-on' : undefined}
                onClick={() => onMode('map')}
                aria-label={t('Carte')}
              >
                <IconMap size={16} />
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
